import React, { useCallback, useEffect } from 'react';
import Styled from './styles';

interface VolumeSlideProps {
  onVolumeChanged: (volume: number) => void;
  onMuted: (muted: boolean) => void;
  volume: number;
  muted: boolean;
  hideVolume: boolean;
}

const VolumeSlide: React.FC<VolumeSlideProps> = ({
  onVolumeChanged,
  onMuted,
  volume,
  muted,
  hideVolume,
}) => {
  const [volumeState, setVolume] = React.useState(volume);
  const [mutedState, setMuted] = React.useState(muted);

  const volumeBeforeMute = React.useRef<number>(0);

  const getVolumeIcon = useCallback(() => {
    if ((mutedState || muted) || volumeState <= 0) return 'volume_off';

    if (volumeState <= 0.25) return 'volume_mute';

    if (volumeState <= 0.75) return 'volume_down';

    return 'volume_up';
  }, [mutedState, volumeState, muted]);

  const handleOnChange = useCallback((volume: number, mute: boolean) => {
    onVolumeChanged(volume);
    setVolume(volume);
    defineMuteState(mute, volume);
    
    // If user is increasing volume, try to enable autoplay
    if (volume > 0 && mutedState) {
      // Attempt to unmute and enable audio
      setMuted(false);
      onMuted(false);
    }
  }, [onVolumeChanged, mutedState, onMuted]);

  const defineMuteState = useCallback((mute: boolean, volume: number) => {
    if (mute && volume > 0) { // unmute if volume was raised during mute
      setMuted(false);
      onMuted(false);
    } else if (volume <= 0) { // mute if volume is turned to 0
      setMuted(true);
      onMuted(true);
    }
  }, [onMuted]);

  // Handle volume button click with better autoplay handling
  const handleVolumeButtonClick = useCallback(() => {
    if (!muted) {
      volumeBeforeMute.current = volumeState;
      // Mute the player
      onVolumeChanged(0);
      setVolume(0);
      setMuted(true);
      onMuted(true);
    } else {
      // Unmute and restore previous volume
      const restoreVolume = volumeBeforeMute.current || volume;
      onVolumeChanged(restoreVolume);
      setVolume(restoreVolume);
      setMuted(false);
      onMuted(false);
      
      // Try to enable autoplay if it was blocked
      if (restoreVolume > 0) {
        // Trigger a user interaction to enable autoplay
        const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
        if (audioContext.state === 'suspended') {
          audioContext.resume();
        }
      }
    }
  }, [muted, volumeState, onVolumeChanged, onMuted]);

  useEffect(() => {
    setMuted(muted);
    volumeBeforeMute.current = volume;
  }, []);

  useEffect(() => {
    if (volumeState !== volume) {
      handleOnChange(volume, muted);
    }

    if (mutedState !== muted) {
      setMuted(muted);
    }
  }, [volume, muted]);

  if (hideVolume) return null;

  return (
    <Styled.Slider>
      <Styled.Volume onClick={handleVolumeButtonClick}
      >
        <i
          tabIndex={-1}
          className={`icon-bbb-${getVolumeIcon()}`}
        />
      </Styled.Volume>
      <Styled.VolumeSlider
        type="range"
        min={0}
        max={1}
        step={0.02}
        value={mutedState ? 0 : volumeState}
        onChange={(e) => handleOnChange(e.target.valueAsNumber, muted)}
      />
    </Styled.Slider>
  );
};

export default VolumeSlide;
